import { useCallback, useEffect, useRef } from 'react'
import { RegisteredRouter } from '@tanstack/react-router'
import { useUserInactivityManager } from './use-user-inactivity-manager'
import { DEFAULT_CHECK_INACTIVITY_DELAY, DEFAULT_EVENTS } from './constants'

export const useInitUserInactivityManager = ({
  events = DEFAULT_EVENTS,
  router,
}: {
  events?: (keyof WindowEventMap)[]
  inactivityTime?: number
  debounceDelay?: number
  router?: RegisteredRouter
} = {}) => {
  const { resetUserInactivityTimer, checkUserInactivityTimer } =
    useUserInactivityManager({ router })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const initializeUserInactivityTimer = useCallback(() => {
    resetUserInactivityTimer()
    events.forEach((event) => {
      window.addEventListener(event, resetUserInactivityTimer)
    })
  }, [resetUserInactivityTimer, events])

  const cleanupUserInactivityTimer = useCallback(() => {
    events.forEach((event) => {
      window.removeEventListener(event, resetUserInactivityTimer)
    })
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [resetUserInactivityTimer, events])

  useEffect(() => {
    initializeUserInactivityTimer()
    return () => cleanupUserInactivityTimer()
  }, [initializeUserInactivityTimer, cleanupUserInactivityTimer])

  useEffect(() => {
    const interval = setInterval(
      checkUserInactivityTimer,
      DEFAULT_CHECK_INACTIVITY_DELAY,
    )
    return () => clearInterval(interval)
  }, [checkUserInactivityTimer])
}
